import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import App from './App'
import NotFound from './NotFound'
import TradeDetail from './TradeDetail'
import StrategyDetail from './StrategyDetail'
import PublicTrade from './PublicTrade'
import AuthCallback from './components/AuthCallback'
import Documentation from './Documentation'
import PrivacyPolicy from './PrivacyPolicy'
import TermsAndConditions from './TermsAndConditions'
import RiskWarning from './RiskWarning'
import Contact from './Contact'
import PublicTools from './PublicTools'
import Pricing from './Pricing'
import ResetPassword from './ResetPassword'
import DashboardLayout from './components/DashboardLayout'
import LandingPage from './LandingPage'
import Changelog from './Changelog'
import AppTest from './AppTest'
import AdminApp from './admin/AdminApp'
import Login from './components/Login'
import Register from './components/Register'
import './index.css'
import './lib/i18n'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { DashboardLayoutProvider } from './contexts/DashboardLayoutContext'
import { SupabaseProvider } from './contexts/SupabaseContext'
import ErrorBoundary from './components/ErrorBoundary'
import { getAdminAppUrl, isAdminApp } from './lib/adminApp'
import { adminSupabase, supabase } from './lib/supabase'
import { revealAppWhenReady } from './lib/revealApp'

// Check if root element exists
const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found. Make sure index.html has a <div id="root"></div> element.')
}

// Log environment info for debugging (only in production)
if (import.meta.env.PROD) {
  console.log('🚀 TraderHub24 - Production Build')
  console.log('Environment:', {
    mode: import.meta.env.MODE,
    prod: import.meta.env.PROD,
    dev: import.meta.env.DEV,
  })
}

// Redirect /blog and /blog/* to static blog (full page load for static HTML)
const BlogRedirect = () => {
  const location = useLocation()
  useEffect(() => {
    const path = location.pathname
    // /blog or /blog/ -> index.html
    if (path === '/blog' || path === '/blog/') {
      window.location.replace('/blog/index.html')
      return
    }
    // Legacy URL redirect: /blog/best-trading-journal-apps.html -> new location
    if (path === '/blog/best-trading-journal-apps.html') {
      window.location.replace('/blog/knowledge/best-trading-journal-apps')
      return
    }
    // /blog/metrics/, /blog/knowledge/ etc. -> add index.html; article URLs stay as-is
    if (path.startsWith('/blog/')) {
      const target = path.endsWith('/') ? path + 'index.html' : path
      window.location.replace(target)
    }
  }, [location.pathname])
  return null
}

// Landing route: / shows LandingPage. Both guests and logged-in users can browse.
const LandingRoute = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('signup') === '1') setShowRegister(true)
    if (params.get('login') === '1') setShowLogin(true)
  }, [location.search])

  return (
    <>
      <LandingPage
        onNavigate={(page) => {
          window.scrollTo(0, 0)
          if (page === 'changelog') navigate('/changelog')
          else if (page === 'documentation') window.location.href = '/documentation'
          else if (page === 'privacy-policy') window.location.href = '/privacy'
          else if (page === 'terms-and-conditions') window.location.href = '/terms'
          else if (page === 'risk-warning') window.location.href = '/risk-warning'
          else if (page === 'contact') window.location.href = '/contact'
        }}
        onRegister={() => setShowRegister(true)}
        onLogin={() => setShowLogin(true)}
      />
      {showLogin && <Login onClose={() => setShowLogin(false)} onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true) }} onSuccess={() => navigate('/app')} />}
      {showRegister && <Register onClose={() => setShowRegister(false)} onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true) }} />}
    </>
  )
}

// Wrapper component for TradeDetail that handles embedded mode.
// Unauthenticated users visiting /app/trade/:detailId get 404.
const TradeDetailWrapper = () => {
  const { user } = useAuth()

  if (!user) {
    return <Navigate to="/404" replace />
  }

  return (
    <DashboardLayout onAddTrade={() => {}}>
      <TradeDetail embedded={true} />
    </DashboardLayout>
  )
}

// Wrapper for StrategyDetail - show inside dashboard layout when logged in.
const StrategyDetailWrapper = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  if (!user) {
    return <Navigate to="/app/strategies" replace />
  }

  return (
    <DashboardLayout onAddTrade={() => navigate('/app/add-trade')}>
      <StrategyDetail onBack={() => navigate('/app/strategies')} embedded />
    </DashboardLayout>
  )
}

const AdminPanelRedirect = () => {
  useEffect(() => {
    window.location.replace(getAdminAppUrl('/'))
  }, [])
  return <div className="app-test-status">Redirecting to admin…</div>
}

const AdminRootApp = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/*" element={<AdminApp />} />
    </Routes>
  </BrowserRouter>
)

const RootApp = () => (
  <BrowserRouter>
    <Routes>
      {/* Public routes - no login required */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="/changelog" element={<Changelog onBack={() => window.location.href = '/'} onNavigate={(page) => {
        if (page === 'privacy-policy') window.location.href = '/privacy';
        else if (page === 'terms-and-conditions') window.location.href = '/terms';
        else if (page === 'risk-warning') window.location.href = '/risk-warning';
        else if (page === 'documentation') window.location.href = '/documentation';
        else if (page === 'contact') window.location.href = '/contact';
        else if (page === 'home') window.location.href = '/';
        else if (page === 'changelog') window.location.href = '/changelog';
      }} />} />
      <Route path="/documentation" element={<Documentation onBack={() => window.location.href = '/'} />} />
      <Route path="/privacy" element={<PrivacyPolicy onBack={() => window.location.href = '/'} />} />
      <Route path="/terms" element={<TermsAndConditions onBack={() => window.location.href = '/'} />} />
      <Route path="/risk-warning" element={<RiskWarning onBack={() => window.location.href = '/'} />} />
      <Route path="/contact" element={<Contact onBack={() => window.location.href = '/'} />} />
      <Route path="/pricing" element={<Pricing onNavigate={(page) => {
        if (page === "home") window.location.href = '/';
        else if (page === "changelog") window.location.href = '/changelog';
        else if (page === "documentation") window.location.href = '/documentation';
        else if (page === "privacy-policy") window.location.href = '/privacy';
        else if (page === "terms-and-conditions") window.location.href = '/terms';
        else if (page === "risk-warning") window.location.href = '/risk-warning';
        else if (page === "contact") window.location.href = '/contact';
      }} onRegister={() => window.location.href = '/?signup=1'} onLogin={() => window.location.href = '/'} />} />
      <Route path="/tools" element={<PublicTools onBack={() => window.location.href = '/'} />} />
      <Route path="/docs" element={<Documentation onBack={() => window.location.href = '/'} />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/p/:slug" element={<PublicTrade />} />
      <Route path="/blog/*" element={<BlogRedirect />} />
      <Route path="/404" element={<NotFound />} />

      {/* Protected /app routes - login required */}
      <Route path="/app-test" element={<AppTest />} />
      <Route path="/app-test/trade/:detailId" element={<AppTest />} />
      <Route path="/app-test/*" element={<AppTest />} />
      <Route path="/app" element={<App />} />
      <Route path="/app/all-trades" element={<App />} />
      <Route path="/app/add-trade" element={<App />} />
      <Route path="/app/create-strategy" element={<App />} />
      <Route path="/app/edit-trade/:id" element={<App />} />
      <Route path="/app/analytics" element={<App />} />
      <Route path="/app/insights" element={<App />} />
      <Route path="/app/accounts" element={<App />} />
      <Route path="/app/tools" element={<App />} />
      <Route path="/app/strategies" element={<App />} />
      <Route path="/app/social-trade" element={<App />} />
      <Route path="/app/social-trade/trade-pools" element={<App />} />
      <Route path="/app/social-trade/followers" element={<App />} />
      <Route path="/app/account" element={<App />} />
      <Route path="/app/admin-panel" element={<AdminPanelRedirect />} />
      <Route path="/app/community" element={<App />} />
      <Route path="/app/trade/:detailId" element={<TradeDetailWrapper />} />
      <Route path="/app/strategy/:strategyName" element={<StrategyDetailWrapper />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  </BrowserRouter>
)

function AppBootstrap() {
  const admin = isAdminApp()
  if (admin) {
    return (
      <SupabaseProvider client={adminSupabase}>
        <AuthProvider>
          <AdminRootApp />
        </AuthProvider>
      </SupabaseProvider>
    )
  }
  return (
    <SupabaseProvider client={supabase}>
      <AuthProvider>
        <DashboardLayoutProvider>
          <NotificationProvider>
            <RootApp />
          </NotificationProvider>
        </DashboardLayoutProvider>
      </AuthProvider>
    </SupabaseProvider>
  )
}

try {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <AppBootstrap />
      </ErrorBoundary>
    </React.StrictMode>,
  )
  void revealAppWhenReady(rootElement)
} catch (error) {
  console.error('Failed to render app:', error)
  rootElement.classList.add('app-visible')
  document.getElementById('app-boot-splash')?.setAttribute('hidden', '')
  rootElement.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; flex-direction: column; padding: 20px; text-align: center;">
      <h1 style="font-size: 24px; margin-bottom: 16px; color: #1f2937;">Failed to Load Application</h1>
      <p style="color: #6b7280; margin-bottom: 16px;">An error occurred while loading the application.</p>
      <button onclick="window.location.reload()" style="padding: 8px 16px; background: #f97316; color: white; border: none; border-radius: 8px; cursor: pointer;">
        Reload Page
      </button>
      <details style="margin-top: 20px; text-align: left; max-width: 600px;">
        <summary style="cursor: pointer; color: #6b7280;">Error Details</summary>
        <pre style="background: #f3f4f6; padding: 16px; border-radius: 8px; overflow: auto; margin-top: 8px; font-size: 12px;">${error instanceof Error ? error.stack : String(error)}</pre>
      </details>
    </div>
  `
}

